import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { ApiError } from "@/api/errors";
import { useSession } from "@/auth/session";
import { DOMAINS } from "./nav";

/**
 * Many crossbar modules are not autoloaded (doc/engineering/switchboard/crossbar-api.md).
 * Probe each section's noun once per session and hide the ones that 404.
 */
const nounsOf = (probe: string | string[] | undefined): string[] => (probe === undefined ? [] : Array.isArray(probe) ? probe : [probe]);

/** The crossbar nouns the sections ask for, each with the scope it lives at. */
const probedNouns = () => {
  const seen = new Map<string, "account" | "platform">();
  for (const d of DOMAINS) for (const s of d.sections) for (const noun of nounsOf(s.probe)) if (!seen.has(noun)) seen.set(noun, d.scope);
  return [...seen].map(([noun, scope]) => ({ noun, scope }));
};

/** The nouns this cluster does not serve. One query for the whole console. */
export function useMissingModules(): Set<string> {
  const accountId = useSession((s) => s.session?.accountId);
  const q = useQuery({
    queryKey: ["capabilities", accountId],
    enabled: Boolean(accountId),
    staleTime: Infinity,
    queryFn: async () => {
      const missing = new Set<string>();
      await Promise.all(
        probedNouns().map(async (p) => {
          const path = p.scope === "platform" ? `/${p.noun}` : `/accounts/${accountId}/${p.noun}`;
          try {
            await api.get(path, { query: { page_size: 1 } });
          } catch (err) {
            // 404: the module is not loaded on this cluster. A 403 stays
            // visible: the screen then says in words that this operator may
            // not use it, which is more useful than a missing entry.
            if (err instanceof ApiError && err.status === 404) missing.add(p.noun);
          }
        }),
      );
      return [...missing];
    },
  });
  return React.useMemo(() => new Set(q.data ?? []), [q.data]);
}

/** The sections to hide: any whose probed noun the cluster does not serve. */
export function useCapabilities(): Set<string> {
  const missing = useMissingModules();
  return React.useMemo(() => {
    const hidden = new Set<string>();
    if (missing.size === 0) return hidden;
    for (const d of DOMAINS) for (const s of d.sections) if (nounsOf(s.probe).some((noun) => missing.has(noun))) hidden.add(s.id);
    return hidden;
  }, [missing]);
}
